import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Chip, ChipSizes, ChipVariants } from '@leegality/leegality-react-component-library';
import { Icon, IconColors, IconKeys } from '@components/icon';
import { useIsOneTimeNotice } from '@app/store';
import { ChannelLockType, GrantStatus } from '@constants';
import { CHANNEL_ICONS, CHANNEL_ORDER, computeRollup } from './channel-utils';

// Renders the ordered chip strip using the cross-purpose rollup. Each chip
// toggles ENABLED/DISABLED via `onChannelToggle`. AMBER-locked chips ignore
// clicks and show a lock badge overlay.
const ChannelChipStrip = ({
  brandColor, channels, profilePurposes, onChannelToggle, visibleField,
}) => {
  const isOneTimeNotice = useIsOneTimeNotice();

  // Precompute the ordered chip list + rollup state once per prop change.
  // The component isn't memoized, so this hoist keeps parent re-renders
  // (which fire on unrelated store slices) from re-doing the O(N*M) ordering
  // and per-channel `computeRollup` work.
  const chipItems = useMemo(() => CHANNEL_ORDER
    .map((id) => channels.find((c) => c.id === id))
    .filter(Boolean)
    .map((channel) => {
      const iconName = CHANNEL_ICONS[channel.id];
      if (!iconName) return null;
      const { selected, lock } = computeRollup(channel.id, profilePurposes, visibleField);
      return {
        channel,
        iconName,
        selected,
        lock,
        fullyLocked: lock === ChannelLockType.AMBER,
      };
    })
    .filter(Boolean), [channels, profilePurposes, visibleField]);

  if (chipItems.length === 0) return null;

  return (
    <div
      className="consent-sdk-channel-chips flex flex-wrap gap-2"
      style={{ '--brand-color': brandColor }}
    >
      {chipItems.map(({
        channel, iconName, selected, lock, fullyLocked,
      }) => {
        const iconColor = selected ? brandColor : IconColors.GRAY;
        const isDisabled = fullyLocked || isOneTimeNotice;
        const handleClick = () => {
          if (isDisabled) return;
          onChannelToggle(channel.id, selected ? GrantStatus.REVOKED : GrantStatus.GRANTED);
        };
        return (
          <span key={channel.id} className={`relative inline-flex${lock ? ' locked-chip' : ''}`}>
            <Chip
              label={channel.label || channel.id}
              variant={ChipVariants.OUTLINED}
              size={ChipSizes.SMALL}
              selected={selected}
              onClick={isDisabled ? undefined : handleClick}
              disabled={isDisabled}
              renderIcon={() => <Icon iconName={iconName} color={iconColor} size={12} />}
            />
            {lock && (
              <span
                className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 pointer-events-none"
              >
                <Icon iconName={IconKeys.Lock} size={10} color={IconColors.GRAY} />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

ChannelChipStrip.propTypes = {
  brandColor: PropTypes.string.isRequired,
  channels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
  })).isRequired,
  profilePurposes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChannelToggle: PropTypes.func,
  visibleField: PropTypes.string,
};

ChannelChipStrip.defaultProps = {
  onChannelToggle: () => {},
  visibleField: 'showInNotice',
};

export default ChannelChipStrip;
